/**
 * Family profile list for the account screen.
 * Tapping a row opens the profile edit flow at /profile/[pipedriveId].
 */
import type { Profile } from "@spork/s4p-server-sdk";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import { Text } from "@/components/ui/text";
import { TEST_IDS } from "@/testIdDefinition";

import { UserIcon } from "@/components/SectionItemIcon";
import { Badge } from "@/components/ui/badge";
import { SettingsChevron } from "@/components/ui/settings";
import {
    formatProfileSummary,
    getProfileLabel,
} from "../lib/formatProfileSummary";

type FamilyProfileRowProps = {
    profile: Profile;
    badge: string | null;
    onPress: () => void;
};

export function FamilyProfileRow({
    profile,
    badge,
    onPress,
}: FamilyProfileRowProps) {
    const { t } = useTranslation();
    const roleFallback =
        profile.role === "student"
            ? t("profile.badge.student")
            : profile.role === "other"
              ? t("profile.badge.other")
              : t("profile.badge.parent");
    const subtitle = formatProfileSummary(profile) ?? roleFallback;

    return (
        <Pressable
            testID={TEST_IDS[TEST_IDS.FAMILY_PROFILE_ROW]}
            accessibilityRole="button"
            className="flex-row items-center justify-between px-4 py-3 gap-2"
            onPress={onPress}
        >
            {profile.role === "student" ? <UserIcon /> : null}
            <View className="flex-1 gap-0.5 pr-3">
                <View className="flex-row items-center gap-2">
                    <Text
                        variant="bold"
                        testID={TEST_IDS[TEST_IDS.FAMILY_PROFILE_ROW_NAME]}
                    >
                        {getProfileLabel(profile)}
                    </Text>
                    {badge ? (
                        <Badge variant="secondary">
                            <Text
                                testID={
                                    TEST_IDS[TEST_IDS.FAMILY_PROFILE_ROW_BADGE]
                                }
                            >
                                {badge}
                            </Text>
                        </Badge>
                    ) : null}
                </View>
                {subtitle ? (
                    <Text
                        variant="muted"
                        testID={TEST_IDS[TEST_IDS.FAMILY_PROFILE_ROW_SUBTITLE]}
                    >
                        {subtitle}
                    </Text>
                ) : null}
            </View>
            <SettingsChevron />
        </Pressable>
    );
}
